<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="450">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="record">Update "{{ record.name }}" Model</h4>
                    <h4 v-else>New Model</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="pb-0" cols="12">
                            <v-img v-if="image" :src="image" class="mb-3" width="200"/>
                            <v-file-input v-model="formData['file']" accept="image/*"
                                          density="compact" label="Select Image"
                                          @change="imagePreview" @click:clear="image=null"/>
                        </v-col>
                        <v-col cols="12">
                            <v-select v-model="formData['vehicle_manufacturer_id']" :items="manufacturers"
                                      density="compact" item-title="name"
                                      item-value="id" label="Vehicle Manufacturer"/>
                            <v-select v-model="formData['asset_type_id']" :items="assetTypes"
                                      density="compact" item-title="name"
                                      item-value="id" label="Asset Type"/>
                            <v-text-field v-model="formData['name']"
                                          :rules="[vRules.required,vRules.maxLength(100)]"
                                          autofocus density="compact" label="Name"/>
                            <v-text-field v-model="formData['wmi_prefix']" density="compact"
                                          label="WMI Prefix" maxlength="30"/>
                            <v-text-field v-model="formData['fuel_tank_size']" density="compact"
                                          label="Fuel Tank Size" type="number"/>
                            <v-select v-model="formData['fuel_tank_unit']" :items="['liters','gallons']"
                                      density="compact"
                                      label="Fuel Tank Unit"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted, onUnmounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    record: Object,
});
const formData = reactive({
    valid: false,
    file: null,
    vehicle_manufacturer_id: props.record ? parseInt(props.record.vehicle_manufacturer_id) : null,
    asset_type_id: props.record ? parseInt(props.record.asset_type_id) : null,
    name: props.record?.name ?? null,
    wmi_prefix: props.record?.wmi_prefix ?? null,
    fuel_tank_size: props.record?.fuel_tank_size ?? null,
    fuel_tank_unit: props.record?.fuel_tank_unit ?? null,
    _method: props.record ? 'put' : 'post',
});
const emits = defineEmits(['update:modelValue', 'addRecord', 'updateRecord']);
const dialogSwitch = ref(props.modelValue);
const manufacturers = ref([]);
const errors = ref([]);
const assetTypes = ref([]);
const image = ref(null);

onMounted(() => {
    Promise.all([fetchManufacturers(), fetchAssetTypes()])
        .finally(() => formReady.value = true);
})

const imagePreview = () => {
    image.value = URL.createObjectURL(formData['file']);
}
const fetchManufacturers = () => {
    return send('get', `fleet/manufacturers`)
        .then((response) => {
            manufacturers.value = response.data.data.data ?? [];
        })
        .catch((error) => {
            showSnackbar('Manufacturers list could not be loaded', 'error')
        })
}
const fetchAssetTypes = () => {
    return send('get', `asset-types`, {list: true, type: 'vehicle'})
        .then((response) => {
            assetTypes.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Asset Types could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    send('post', `fleet/models${props.record ? '/' + props.record.id : ''}`, formData, {
        headers: {
            'Content-Type': "multipart/form-data"
        },
    }).then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            if (image.value) {
                response.data.data.image = image.value;
            }
            emits(props.record ? 'updateRecord' : 'addRecord', response.data.data);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response?.data?.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response?.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    })
}

</script>
